import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Divider,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import FormatClearIcon from '@mui/icons-material/FormatClear';
import DOMPurify from 'dompurify';
import { config } from '../config';

const editorContentStyle = `
  .astro-rich-editor-body {
    min-height: 420px;
    padding: 18px;
    color: #fff;
    background: #151515;
    border: 1px solid rgba(245, 151, 79, 0.45);
    border-radius: 0 0 8px 8px;
    outline: none;
    font-family: Roboto, Helvetica, Arial, sans-serif;
    font-size: 1rem;
    line-height: 1.7;
    overflow-y: auto;
  }
  .astro-rich-editor-body:focus {
    border-color: #f5974f;
    box-shadow: 0 0 0 1px rgba(245,151,79,0.35);
  }
  .astro-rich-editor-body p,
  .astro-rich-editor-body div {
    margin: 0 0 1em;
  }
  .astro-rich-editor-body h2,
  .astro-rich-editor-body h3 {
    color: #f5974f;
    margin: 1.1em 0 0.55em;
  }
  .astro-rich-editor-body a,
  .astro-rich-editor-body a:visited {
    color: #f5974f;
  }
  .astro-rich-editor-body a:hover {
    color: #ffb06f;
  }
  .astro-rich-editor-body img {
    max-width: 100%;
    height: auto !important;
    display: block;
    margin: 1em 0;
    border-radius: 6px;
  }
`;

const normalizeContent = (html) => {
  if (!html || !html.trim()) return '<p><br></p>';
  return DOMPurify.sanitize(html, {
    ADD_ATTR: ['target', 'rel', 'data-original-src', 'loading', 'decoding'],
    ADD_TAGS: ['iframe']
  });
};

const RichTextEditor = ({ value, onChange, style = {}, minHeight = 420 }) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastValueRef = useRef('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const incoming = normalizeContent(value || '');
    if (incoming !== lastValueRef.current && incoming !== editor.innerHTML) {
      editor.innerHTML = incoming;
      lastValueRef.current = incoming;
    }
  }, [value]);

  const emitChange = () => {
    const html = editorRef.current?.innerHTML || '';
    lastValueRef.current = html;
    onChange?.(html);
  };

  const focusEditor = () => {
    editorRef.current?.focus();
  };

  const runCommand = (command, commandValue = null) => {
    focusEditor();
    document.execCommand(command, false, commandValue);
    emitChange();
  };

  const applyBlock = (tag) => {
    runCommand('formatBlock', tag);
  };

  const addLink = () => {
    focusEditor();
    const selected = window.getSelection()?.toString() || '';
    const url = window.prompt('Paste the link URL');
    if (!url) return;
    document.execCommand('createLink', false, url);

    const selection = window.getSelection();
    const anchor = selection?.anchorNode?.parentElement?.closest?.('a');
    if (anchor) {
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      if (!selected) anchor.textContent = url;
    }
    emitChange();
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    setUploading(true);

    try {
      const response = await fetch(`${config.endpoints.news}/upload`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error('Image upload failed');
      }

      const image = await response.json();
      const originalSrc = image.originalUrl || image.url;
      focusEditor();
      document.execCommand(
        'insertHTML',
        false,
        `<p><img src="${originalSrc}" data-original-src="${originalSrc}" loading="lazy" decoding="async" class="img-fluid" alt="" /></p><p><br></p>`
      );
      emitChange();
    } catch (error) {
      console.error('News editor image upload failed:', error);
      window.alert('Image upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const editorHeight = typeof style?.height === 'number'
    ? style.height
    : typeof style?.height === 'string'
      ? style.height
      : minHeight;

  return (
    <Paper
      elevation={0}
      sx={{
        background: '#202020',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: '8px',
        overflow: 'hidden'
      }}
    >
      <style>{editorContentStyle}</style>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
          p: 1,
          background: '#2b2b2b',
          borderBottom: '1px solid rgba(255,255,255,0.12)'
        }}
      >
        <ButtonGroup variant="outlined" size="small">
          <Tooltip title="Paragraph"><Button onClick={() => applyBlock('P')}>P</Button></Tooltip>
          <Tooltip title="Heading"><Button onClick={() => applyBlock('H2')}>H2</Button></Tooltip>
          <Tooltip title="Subheading"><Button onClick={() => applyBlock('H3')}>H3</Button></Tooltip>
        </ButtonGroup>

        <ButtonGroup variant="outlined" size="small">
          <Tooltip title="Bold">
            <IconButton size="small" onClick={() => runCommand('bold')}><FormatBoldIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Italic">
            <IconButton size="small" onClick={() => runCommand('italic')}><FormatItalicIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Bullet list">
            <IconButton size="small" onClick={() => runCommand('insertUnorderedList')}><FormatListBulletedIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Numbered list">
            <IconButton size="small" onClick={() => runCommand('insertOrderedList')}><FormatListNumberedIcon fontSize="small" /></IconButton>
          </Tooltip>
        </ButtonGroup>

        <ButtonGroup variant="outlined" size="small">
          <Tooltip title="Add link">
            <IconButton size="small" onClick={addLink}><LinkIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Add image">
            <IconButton size="small" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
              <ImageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear formatting">
            <IconButton size="small" onClick={() => runCommand('removeFormat')}><FormatClearIcon fontSize="small" /></IconButton>
          </Tooltip>
        </ButtonGroup>

        <Divider flexItem orientation="vertical" sx={{ mx: 0.5 }} />
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.68)' }}>
          {uploading ? 'Uploading image…' : 'Visual editor — no HTML editing required'}
        </Typography>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleImageUpload}
        />
      </Box>

      <Box
        ref={editorRef}
        className="astro-rich-editor-body"
        contentEditable
        suppressContentEditableWarning
        onInput={emitChange}
        onBlur={emitChange}
        sx={{ minHeight: editorHeight }}
        role="textbox"
        aria-label="Article content editor"
      />
    </Paper>
  );
};

export default RichTextEditor;
